/**
 * Error message utilities for user-friendly error display.
 * Maps HTTP codes and backend error codes to human-readable messages.
 */

// HTTP status code to user-friendly message mapping
export const HTTP_ERROR_MESSAGES: Record<number, string> = {
  400: 'Invalid request. Please check your input and try again.',
  401: 'Your session has expired. Please log in again.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested item was not found.',
  409: 'This item already exists or conflicts with existing data.',
  422: 'The data you provided is invalid. Please check your input.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'An internal server error occurred. Please try again later.',
  502: 'Server is temporarily unavailable. Please try again later.',
  503: 'Service is temporarily unavailable. Please try again later.',
  504: 'Server response took too long. Please try again later.',
};

// Backend error code to user-friendly message mapping
// These are specific error codes returned by the API
export const BACKEND_ERROR_MESSAGES: Record<string, string> = {
  // Authentication errors
  UNAUTHORIZED: 'Invalid email or password. Please check your credentials.',
  INVALID_CREDENTIALS: 'Invalid email or password. Please check your credentials.',
  TOKEN_EXPIRED: 'Your session has expired. Please log in again.',
  INVALID_TOKEN: 'Your session is invalid. Please log in again.',
  REFRESH_TOKEN_EXPIRED: 'Your session has expired. Please log in again.',
  
  // Validation errors
  VALIDATION_ERROR: 'The data you provided is invalid. Please check the form for errors.',
  WEAK_PASSWORD: 'This password is too common. Please choose a stronger password.',
  
  // Permission errors
  FORBIDDEN: 'You do not have permission to perform this action.',
  NOT_FOUND: 'The requested item was not found.',
  
  // Conflict errors
  CONFLICT: 'This item already exists or conflicts with existing data.',
  DUPLICATE_ENTRY: 'This item already exists.',
  
  // Generic errors
  INTERNAL_ERROR: 'An unexpected error occurred. Please try again later.',
  NETWORK_ERROR: 'Unable to connect to the server. Please check your connection.',
};

/**
 * Maps validation error field paths to user-friendly field names.
 */
export const FIELD_NAME_MAP: Record<string, string> = {
  email: 'Email',
  password: 'Password',
  firstName: 'First name',
  lastName: 'Last name',
  title: 'Title',
  name: 'Name',
  description: 'Description',
  dueDate: 'Due date',
  remindAt: 'Reminder time',
  projectId: 'Project',
  assigneeId: 'Assignee',
  priority: 'Priority',
  labels: 'Labels',
  notes: 'Notes',
  color: 'Color',
};

/**
 * Maps validation error messages to more user-friendly versions.
 */
export const VALIDATION_MESSAGE_MAP: Record<string, string> = {
  'string.min': 'Must be at least {min} characters',
  'string.max': 'Must be at most {max} characters',
  'string.email': 'Please enter a valid email address',
  'string.uuid': 'Please select a valid option',
  'required': 'This field is required',
  'Invalid date format': 'Please enter a valid date',
  'Invalid email address': 'Please enter a valid email address',
  'Passwords do not match': 'Passwords do not match',
  'Password must be at least 8 characters': 'Password must be at least 8 characters',
  'Password must be at least 12 characters': 'Password must be at least 12 characters',
  'Password must contain at least one uppercase letter': 'Password must contain an uppercase letter',
  'Password must contain at least one lowercase letter': 'Password must contain a lowercase letter',
  'Password must contain at least one number': 'Password must contain a number',
  'Password must contain at least one special character': 'Password must contain a special character',
};

/**
 * Extracts a user-friendly error message from an Axios error response.
 * Priority: backend error message > HTTP status message > generic fallback.
 */
export function extractErrorMessage(error: unknown): string {
  // Handle Axios errors with response data
  if (isAxiosError(error)) {
    const response = error.response;
    
    // Network error (no response)
    if (!response) {
      return BACKEND_ERROR_MESSAGES.NETWORK_ERROR;
    }
    
    // Extract error from response body
    const errorData = response.data?.error;
    
    // Backend provided specific error message
    if (errorData?.message) {
      // Check if we have a friendlier mapping for this code
      if (errorData?.code && errorData.code in BACKEND_ERROR_MESSAGES) {
        return BACKEND_ERROR_MESSAGES[errorData.code];
      }
      // Use backend message directly if it's already user-friendly
      return errorData.message;
    }
    
    // HTTP status code mapping
    if (response.status && response.status in HTTP_ERROR_MESSAGES) {
      return HTTP_ERROR_MESSAGES[response.status];
    }
    
    // Fallback to status text or generic message
    return response.statusText || 'An unexpected error occurred. Please try again.';
  }
  
  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message || 'An unexpected error occurred. Please try again.';
  }
  
  // Unknown error type
  return 'An unexpected error occurred. Please try again.';
}

/**
 * Extracts validation error details from an Axios error response.
 * Returns an array of { field, message } objects for display.
 */
export function extractValidationErrors(error: unknown): Array<{ field: string; message: string }> {
  if (!isAxiosError(error)) {
    return [];
  }
  
  const response = error.response;
  if (!response) {
    return [];
  }
  
  const errorData = response.data?.error;
  
  // Check for validation error details
  if (errorData?.code === 'VALIDATION_ERROR' && errorData?.details) {
    return (errorData.details as Array<{ path: string; message: string }>).map(detail => ({
      field: FIELD_NAME_MAP[detail.path] || detail.path,
      message: formatValidationMessage(detail.message),
    }));
  }
  
  // No validation details
  return [];
}

/**
 * Formats a validation message to be more user-friendly.
 */
function formatValidationMessage(message: string): string {
  // Check if we have a mapping for this exact message
  if (VALIDATION_MESSAGE_MAP[message]) {
    return VALIDATION_MESSAGE_MAP[message];
  }
  
  // Handle Zod-style messages with parameters
  // e.g., "string.min: Password must be at least 12 characters"
  const zodMatch = message.match(/^(\w+)\.\w+/);
  if (zodMatch && VALIDATION_MESSAGE_MAP[zodMatch[0]]) {
    return VALIDATION_MESSAGE_MAP[zodMatch[0]];
  }
  
  // Return the original message if no mapping found
  return message;
}

/**
 * Type guard to check if an error is an Axios error.
 */
interface AxiosErrorLike {
  isAxiosError: true;
  response?: {
    data?: {
      error?: {
        code?: string;
        message?: string;
        details?: unknown[];
      };
      message?: string;
    };
    status?: number;
    statusText?: string;
  };
}

function isAxiosError(error: unknown): error is AxiosErrorLike {
  return (
    error !== null &&
    typeof error === 'object' &&
    'isAxiosError' in error &&
    (error as { isAxiosError?: boolean }).isAxiosError === true
  );
}

/**
 * Creates a combined error result with message and validation details.
 */
export function extractErrorResult(error: unknown): {
  message: string;
  validationErrors: Array<{ field: string; message: string }>;
  hasValidationErrors: boolean;
} {
  const message = extractErrorMessage(error);
  const validationErrors = extractValidationErrors(error);
  
  return {
    message,
    validationErrors,
    hasValidationErrors: validationErrors.length > 0,
  };
}

export default {
  extractErrorMessage,
  extractValidationErrors,
  extractErrorResult,
  HTTP_ERROR_MESSAGES,
  BACKEND_ERROR_MESSAGES,
  FIELD_NAME_MAP,
  VALIDATION_MESSAGE_MAP,
};